'use client';

import { LinkStyled } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useAuthClient } from '@root/src/hooks';


// Interface
// ******************************************
interface BundleCardProps {
  bundleData?: ApiSchema<'OfferBundle'>,
}


/**
 * @description Renders a job bundle card. Renders nothing if there is no bundle data.
 * - **bundleData** - *{object}* - the bundle data to render; default undefined
 * - **className** - *{string}* - extra classes for the card; default empty string
 * - **anchorStyling** - *{string}* - the styling for the CTA button; default solid
 * - **anchorColor** - *{string}* - the color for the CTA button; default ink
 * - **iconColor** - *{string}* - the color for the icons; default text-secondary
 *
 * To preselect the desired bundle on the job activation page, we use the selectedBundleId search parameter.
 * like this: *selectedBundleId: bundleData?.packageId*
 */
export const BundleCardCta = (props: BundleCardProps) => {
  // Props destructuring
  const {
    bundleData,
  } = props;


  // Translation
  const { t } = useTranslation('common');

  const { fullPrice: credits, price: discountedCredits } = bundleData || {};
  const isDiscounted = credits !== discountedCredits;

  const { isSafeAuth } = useAuthClient();

  const destinationPath = `/job-add/edit?selectedPackageId=${bundleData?.id}`;


  // Render component
  // ******************************************
  return (
    <div className="border-t-0">
      <div className="flex items-center justify-between">
        <div className="mt-0.5 flex items-end gap-1.5">
          <div className="flex items-end text-5xl font-bold">
            {isDiscounted ? (
              <div className="flex flex-col items-start gap-1">
                <span className="block text-sm text-error line-through">{Math.ceil(credits as number)}</span>
                <span>{Math.ceil(discountedCredits as number)}</span>
              </div>
            ) : (
              <span>{credits}</span>
            )}

            {bundleData?.bundlePriceType === 'flex' && '%'}
          </div>
          <span className="lowercase text-ink-medium">
            {bundleData?.bundlePriceType === 'flex' ? t('global.label.from_salary') : t('services.page.currency') }
            {bundleData?.bundlePriceType === 'flex' && bundleData?.type === 'standard' ? t('global.label.minimum.neuro.required') : ''}
          </span>
          {' '}
          <span className="text-ink-medium">+ TVA</span>

        </div>

      </div>
      <LinkStyled
        href={isSafeAuth
          ? destinationPath
          : `/login?targetPath=${encodeURIComponent(destinationPath)}`}
        styling="none"
        rounding="full"
        size="sm"
        className="mt-3 block w-fit rounded-full bg-secondary px-6 py-3 text-sm font-medium text-surface"
      >
        {t('global.label.activate.job')}
      </LinkStyled>

    </div>
  );
};
